import React, { useState, useEffect } from 'react';
import { Microscope, Plus, X, Loader2, Edit, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import ConfirmDialog from '../shared/ConfirmDialog';

const infectionTypes = ['CLABSI', 'CAUTI', 'SSI', 'VAP', 'C. diff', 'MRSA', 'COVID-19', 'Influenza', 'Other'];
const emptyForm = { infection_type: '', department: '', unit: '', onset_date: '', device_related: false, device_type: '', organism: '', notes: '', patient_id: '' };

const InfectionControlPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const loadRecords = () => { setLoading(true); api.get('/api/infections').then(setRecords).catch(e => toast.error(e.message)).finally(() => setLoading(false)); };
  useEffect(() => { loadRecords(); }, []);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setShowModal(true); };
  const openEdit = (r) => {
    setForm({ infection_type: r.infection_type || '', department: r.department || '', unit: r.unit || '', onset_date: r.onset_date || '', device_related: r.device_related || false, device_type: r.device_type || '', organism: r.organism || '', notes: r.notes || '', patient_id: r.patient_id || '' });
    setEditingId(r.id); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.infection_type || !form.department || !form.onset_date) { toast.error('Fill required fields'); return; }
    setSubmitting(true);
    try {
      if (editingId) { await api.put(`/api/infections/${editingId}`, form); toast.success('Record updated'); }
      else { await api.post('/api/infections', form); toast.success('Record created'); }
      setShowModal(false); setForm(emptyForm); setEditingId(null); loadRecords();
    } catch (e) { toast.error(e.message); } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return; setDeleting(true);
    try { await api.delete(`/api/infections/${deleteTarget.id}`); toast.success('Record deleted'); setDeleteTarget(null); loadRecords(); }
    catch (e) { toast.error(e.message); } finally { setDeleting(false); }
  };

  const typeSummary = infectionTypes.slice(0, 6).map(t => ({ type: t, count: records.filter(r => r.infection_type === t).length }));

  return (
    <div className="space-y-6" data-testid="infection-control-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-gray-900">Infection Prevention & Control</h1><p className="text-gray-500 text-sm mt-1">HAI surveillance, tracking, and reporting</p></div>
        <Button onClick={openCreate} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="report-hai-btn"><Plus className="w-4 h-4" />Report HAI</Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {typeSummary.map(item => (
          <div key={item.type} className="bg-white rounded-xl border border-gray-100 p-4" data-testid="infection-type-card">
            <div className="flex items-center justify-between mb-2"><span className="font-semibold text-gray-900">{item.type}</span><Microscope className="w-4 h-4 text-gray-400" /></div>
            <p className="text-2xl font-bold text-gray-900">{item.count}</p><p className="text-xs text-gray-500">events</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center gap-4">
          <div><p className="text-sm text-gray-500">Total Records</p><p className="text-3xl font-bold text-gray-900" data-testid="total-infections">{records.length}</p></div>
          <div className="ml-8"><p className="text-sm text-gray-500">Device Related</p><p className="text-3xl font-bold text-orange-600">{records.filter(r => r.device_related).length}</p></div>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[var(--niko-teal)]" /></div> : (
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="p-4 border-b border-gray-100"><h2 className="font-semibold text-gray-900">Infection Records</h2></div>
          <div className="divide-y divide-gray-100">
            {records.length === 0 ? <div className="p-8 text-center text-gray-400">No infection records found</div> :
            records.map(record => (
              <div key={record.id} className="p-4 hover:bg-gray-50 transition-colors group" data-testid="infection-record-item">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><Microscope className="w-5 h-5 text-red-600" /></div>
                    <div>
                      <p className="font-medium text-gray-900">{record.infection_type}</p>
                      <p className="text-sm text-gray-500">{record.department}{record.unit ? ` - ${record.unit}` : ''} | Onset: {record.onset_date}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-right mr-2">
                      {record.device_related && <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">Device: {record.device_type || 'Yes'}</span>}
                      {record.organism && <p className="text-xs text-gray-500 mt-1">Organism: {record.organism}</p>}
                    </div>
                    <button onClick={() => openEdit(record)} className="p-2 text-gray-400 hover:text-[var(--niko-teal)] hover:bg-[var(--niko-teal)]/10 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid="edit-infection-btn"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => setDeleteTarget(record)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all" data-testid="delete-infection-btn"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="infection-modal">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{editingId ? 'Edit Record' : 'Report HAI'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><Label>Infection Type *</Label><select className="w-full mt-1 h-10 px-3 rounded-md border border-input bg-background text-sm" value={form.infection_type} onChange={e => setForm({...form, infection_type: e.target.value})} data-testid="infection-type-select"><option value="">Select...</option>{infectionTypes.map(t => <option key={t} value={t}>{t}</option>)}</select></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Department *</Label><Input className="mt-1" value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
                <div><Label>Unit</Label><Input className="mt-1" value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} /></div>
              </div>
              <div><Label>Onset Date *</Label><Input type="date" className="mt-1" value={form.onset_date} onChange={e => setForm({...form, onset_date: e.target.value})} data-testid="onset-date-input" /></div>
              <div><Label>Patient ID</Label><Input className="mt-1" value={form.patient_id} onChange={e => setForm({...form, patient_id: e.target.value})} /></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.device_related} onChange={e => setForm({...form, device_related: e.target.checked})} className="rounded" />Device Related</label>
              {form.device_related && <div><Label>Device Type</Label><Input className="mt-1" value={form.device_type} onChange={e => setForm({...form, device_type: e.target.value})} /></div>}
              <div><Label>Organism</Label><Input className="mt-1" value={form.organism} onChange={e => setForm({...form, organism: e.target.value})} /></div>
              <div><Label>Notes</Label><Textarea className="mt-1" rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex gap-3">
              <Button variant="outline" onClick={() => setShowModal(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="submit-infection-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}{editingId ? 'Update' : 'Submit'}</Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} loading={deleting} title="Delete Infection Record" message={`Delete this ${deleteTarget?.infection_type} record? This cannot be undone.`} />
    </div>
  );
};

export default InfectionControlPage;
